import React, { useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Domain } from "Domain";
import LinearIndeterminate from "components/Loader/LinearLoader";
import thsLogo from "assets/img/logo_ths.webp";
import {
  User,
  Lock,
  Eye,
  EyeSlash,
  Warning,
  SignIn,
  Heartbeat,
} from "@phosphor-icons/react";

const THS_BLUE = "#1a3a8f";
const THS_BLUE_MID = "#2f72f2";
const THS_BLUE_LIGHT = "#4d90fe";

const styles = {
  page: {
    minHeight: "100vh",
    background: `linear-gradient(145deg, ${THS_BLUE} 0%, #1e4fc2 45%, ${THS_BLUE_MID} 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: '"Inter", "Roboto", sans-serif',
    padding: "20px",
    position: "relative",
    overflow: "hidden",
  },
  bgCircle1: {
    position: "absolute",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.04)",
    top: "-120px",
    right: "-120px",
    pointerEvents: "none",
  },
  bgCircle2: {
    position: "absolute",
    width: "320px",
    height: "320px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
    bottom: "-80px",
    left: "-80px",
    pointerEvents: "none",
  },
  bgCircle3: {
    position: "absolute",
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.04)",
    top: "40%",
    left: "10%",
    pointerEvents: "none",
  },
  card: {
    background: "#ffffff",
    borderRadius: "24px",
    boxShadow: "0 32px 80px rgba(10, 30, 100, 0.35)",
    width: "100%",
    maxWidth: "440px",
    overflow: "hidden",
    position: "relative",
    zIndex: 1,
  },
  cardTop: {
    background: `linear-gradient(135deg, ${THS_BLUE} 0%, ${THS_BLUE_MID} 100%)`,
    padding: "36px 36px 32px",
    textAlign: "center",
    position: "relative",
  },
  cardTopAccent: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: `linear-gradient(90deg, ${THS_BLUE_LIGHT}, rgba(255,255,255,0.3), ${THS_BLUE_LIGHT})`,
  },
  logoIconBox: {
    width: "72px",
    height: "72px",
    margin: "0 auto 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.15)",
    borderRadius: "20px",
    border: "2px solid rgba(255,255,255,0.25)",
    backdropFilter: "blur(8px)",
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginBottom: "6px",
  },
  title: {
    color: "#ffffff",
    fontSize: "22px",
    fontWeight: "700",
    margin: "0",
    letterSpacing: "-0.3px",
  },
  subtitle: {
    color: "rgba(255,255,255,0.72)",
    fontSize: "13px",
    margin: "0",
    letterSpacing: "0.2px",
  },
  cardBody: {
    padding: "34px 36px 30px",
  },
  formGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "8px",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  },
  inputWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  inputIconLeft: {
    position: "absolute",
    left: "14px",
    display: "flex",
    alignItems: "center",
    pointerEvents: "none",
    color: "#9ca3af",
    zIndex: 1,
  },
  inputIconRight: {
    position: "absolute",
    right: "14px",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "#9ca3af",
    zIndex: 1,
    background: "none",
    border: "none",
    padding: 0,
  },
  input: {
    width: "100%",
    padding: "13px 44px 13px 44px",
    border: "1.5px solid #e5e7eb",
    borderRadius: "12px",
    fontSize: "14px",
    color: "#111827",
    fontFamily: '"Inter", sans-serif',
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
    boxSizing: "border-box",
    background: "#f9fafb",
  },
  inputFocus: {
    borderColor: THS_BLUE_MID,
    boxShadow: `0 0 0 3px rgba(47,114,242,0.12)`,
    background: "#fff",
  },
  inputError: {
    borderColor: "#ef4444",
    boxShadow: "0 0 0 3px rgba(239,68,68,0.1)",
  },
  errorMsg: {
    color: "#dc2626",
    fontSize: "12px",
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "9px 12px",
    background: "rgba(239,68,68,0.06)",
    borderRadius: "8px",
    border: "1px solid rgba(239,68,68,0.15)",
  },
  btn: {
    width: "100%",
    padding: "14px",
    background: `linear-gradient(135deg, ${THS_BLUE} 0%, ${THS_BLUE_MID} 100%)`,
    color: "#ffffff",
    border: "none",
    borderRadius: "12px",
    fontSize: "15px",
    fontWeight: "600",
    cursor: "pointer",
    fontFamily: '"Inter", sans-serif',
    boxShadow: `0 6px 20px rgba(47,114,242,0.35)`,
    transition: "all 0.2s",
    letterSpacing: "0.3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginTop: "4px",
  },
  spinnerStyle: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.4)",
    borderTop: "2px solid #fff",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.8s linear infinite",
  },
  cardFooter: {
    padding: "14px 36px 22px",
    textAlign: "center",
    borderTop: "1px solid #f3f4f6",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
  },
  footerText: {
    fontSize: "11.5px",
    color: "#9ca3af",
    margin: 0,
  },
  footerBrand: {
    fontWeight: "700",
    color: THS_BLUE_MID,
  },
};

export default function LoginPage({
  setLoggedin,
  setNotification,
  setUserType,
  setUserdetails,
}) {
  const [loading, setloading] = useState(false);
  const [data, setData] = useState({ Username: "", Password: "" });
  const [invalid, setInvalid] = useState(false);
  const [focused, setFocused] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function HandleData(e) {
    const newData = { ...data };
    newData[e.target.id] = e.target.value;
    setData(newData);
    setInvalid(false);
  }

  function HandleSave() {
    setloading(true);
    if (data.Username !== "" && data.Password !== "") {
      let formData = new FormData();
      formData.append("Email", data.Username);
      formData.append("Password", data.Password);
      axios({ method: "post", url: Domain + "/LoginCheck.php", data: formData })
        .then(function (response) {
          if (response.data.success) {
            setData({ Username: "", Password: "" });
            setUserType(response.data.data.Usertype);
            setUserdetails(response.data.data);
            setLoggedin(response.data.data.Usertype);
            setNotification(true);
          } else {
            setInvalid(true);
            setloading(false);
          }
        })
        .catch(function () {
          setInvalid(true);
          setLoggedin(undefined);
          setloading(false);
        });
    } else {
      setInvalid(true);
      setLoggedin(undefined);
      setloading(false);
    }
  }

  const getInputStyle = (field) => ({
    ...styles.input,
    ...(focused === field ? styles.inputFocus : {}),
    ...(invalid ? styles.inputError : {}),
  });

  return (
    <div style={styles.page}>
      <div style={styles.bgCircle1} />
      <div style={styles.bgCircle2} />
      <div style={styles.bgCircle3} />

      <div style={styles.card}>
        {/* ── Card Header ── */}
        <div style={styles.cardTop}>
          <div style={styles.logoIconBox}>
            <img
              src={thsLogo}
              alt="Telemed Healthcare Solutions"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
              }}
            />
          </div>

          <div style={styles.brandRow}>
            <h1 style={styles.title}>THS Billing Portal</h1>
          </div>

          <p style={styles.subtitle}>
            Telemed Healthcare Solutions — Sign in to continue
          </p>
          <div style={styles.cardTopAccent} />
        </div>

        {/* ── Card Body ── */}
        <div style={styles.cardBody}>
          {/* Username */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="Username">
              <User size={13} weight="bold" color="#6b7280" />
              Username
            </label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIconLeft}>
                <User
                  size={18}
                  weight="duotone"
                  color={focused === "Username" ? THS_BLUE_MID : "#9ca3af"}
                />
              </span>
              <input
                id="Username"
                type="text"
                placeholder="Enter your username"
                value={data.Username}
                onChange={HandleData}
                onFocus={() => setFocused("Username")}
                onBlur={() => setFocused("")}
                style={getInputStyle("Username")}
                onKeyPress={(e) => e.key === "Enter" && HandleSave()}
                autoComplete="username"
              />
            </div>
          </div>

          {/* Password */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="Password">
              <Lock size={13} weight="bold" color="#6b7280" />
              Password
            </label>
            <div style={styles.inputWrapper}>
              <span style={styles.inputIconLeft}>
                <Lock
                  size={18}
                  weight="duotone"
                  color={focused === "Password" ? THS_BLUE_MID : "#9ca3af"}
                />
              </span>
              <input
                id="Password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={data.Password}
                onChange={HandleData}
                onFocus={() => setFocused("Password")}
                onBlur={() => setFocused("")}
                style={getInputStyle("Password")}
                onKeyPress={(e) => e.key === "Enter" && HandleSave()}
                autoComplete="current-password"
              />
              <button
                type="button"
                style={styles.inputIconRight}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <EyeSlash size={18} weight="duotone" color="#6b7280" />
                ) : (
                  <Eye size={18} weight="duotone" color="#6b7280" />
                )}
              </button>
            </div>
            {invalid && (
              <p style={styles.errorMsg}>
                <Warning size={15} weight="fill" color="#dc2626" />
                Invalid credentials. Please check your username and password.
              </p>
            )}
          </div>

          {/* Sign In Button */}
          <button
            style={{
              ...styles.btn,
              opacity: loading ? 0.8 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
            onClick={HandleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={styles.spinnerStyle} />
                Signing in...
              </>
            ) : (
              <>
                Sign In
                <SignIn size={18} weight="bold" color="#ffffff" />
              </>
            )}
          </button>
        </div>

        {/* ── Card Footer ── */}
        <div style={styles.cardFooter}>
          <Heartbeat size={14} weight="duotone" color={THS_BLUE_MID} />
          <p style={styles.footerText}>
            © {new Date().getFullYear()}{" "}
            <span style={styles.footerBrand}>Telemed Healthcare Solutions</span>
          </p>
        </div>

        {loading && <LinearIndeterminate />}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

LoginPage.propTypes = {
  setLoggedin: PropTypes.func,
  setNotification: PropTypes.func,
  setUserType: PropTypes.any,
  setUserdetails: PropTypes.any,
};
